import type { RfqPackage, RfqProject, RfqSubmission } from '../types';
import { cncQuoteApprovedAt } from './approvedQuoteBanner';
import { resolveCncAwardedQuote } from './cncAwardedQuote';
import { isRfqQuoteRejected } from './rfqQuoteRejectionTargets';
import {
  approvedSubmissionIdsForReleaseSelection,
  approvedSupplierOrgIdsForReleaseSelection,
  releaseKeyForPackage,
} from './timberRfqPrePortalVersion';
import { normalizeWorkType } from './vendorHelpers';
import type { VendorPortalDealGroup, VendorPortalWorkGroup } from './vendorPortalDealGroups';
import { projectNeedsQuoteResponse, buildVendorPackageVersions } from './vendorPackageVersion';

/**
 * Timber RFQ — submission ids staff approved on this package's version.
 * Approvals are stored on the rfqProject (readable by the supplier), keyed by
 * release; this resolves them the same way the staff panel does.
 */
export function approvedSubmissionIdsForPackage(
  project: Pick<RfqProject, 'approvedSubmissionIds' | 'approvedSubmissionIdsByRelease'>,
  pkg: RfqPackage,
  packages: RfqPackage[],
  submissionsByPackage: Map<string, RfqSubmission[]>,
): Set<string> {
  return new Set(
    approvedSubmissionIdsForReleaseSelection(
      project,
      packages,
      submissionsByPackage,
      releaseKeyForPackage(pkg),
    ),
  );
}

/** CNC — true when this supplier org is awarded the package's release. */
export function isCncPackageApprovedForOrg(
  project: Pick<
    RfqProject,
    'approvedSupplierOrgId' | 'approvedSupplierOrgIdByRelease' | 'approvedSupplierOrgIdsByRelease'
  >,
  pkg: RfqPackage,
  orgId: string | undefined,
): boolean {
  if (!orgId) return false;
  return approvedSupplierOrgIdsForReleaseSelection(project, releaseKeyForPackage(pkg)).includes(
    orgId,
  );
}

/**
 * CNC awards are per supplier org/release, with optional per-submission ids when
 * staff approve structure-scoped streams. Prefer explicit submission approvals;
 * otherwise infer the standing awarded upload for legacy org-only awards.
 */
export function cncAwardedSubmissionIdsForPackage(
  project: Pick<
    RfqProject,
    | 'approvedSupplierOrgId'
    | 'approvedSupplierOrgIdByRelease'
    | 'approvedSupplierOrgIdsByRelease'
    | 'approvedSupplierApprovedAtByRelease'
    | 'approvedSubmissionIds'
    | 'approvedSubmissionIdsByRelease'
  >,
  pkg: RfqPackage,
  orgId: string | undefined,
  quotes: RfqSubmission[],
): Set<string> {
  if (!isCncPackageApprovedForOrg(project, pkg, orgId)) return new Set();

  const quoteIds = new Set(quotes.map((q) => q.id).filter(Boolean) as string[]);
  const explicit = new Set<string>();
  for (const id of project.approvedSubmissionIds ?? []) {
    if (quoteIds.has(id)) explicit.add(id);
  }
  for (const ids of Object.values(project.approvedSubmissionIdsByRelease ?? {})) {
    for (const id of ids ?? []) {
      if (quoteIds.has(id)) explicit.add(id);
    }
  }
  if (explicit.size > 0) return explicit;

  const awarded = resolveCncAwardedQuote({
    quotes,
    approvedAt: cncQuoteApprovedAt(project, releaseKeyForPackage(pkg), orgId),
  });
  return awarded?.id ? new Set([awarded.id]) : new Set();
}

function timberGroupHasApproval(
  group: VendorPortalWorkGroup,
  submissionsByPackage: Map<string, RfqSubmission[]>,
): boolean {
  const { project, packages } = group;
  const approvedIds = new Set<string>(project.approvedSubmissionIds ?? []);
  for (const ids of Object.values(project.approvedSubmissionIdsByRelease ?? {})) {
    for (const id of ids) approvedIds.add(id);
  }
  if (!approvedIds.size) return false;
  // Approvals live on the shared rfqProject — only count ones on this
  // supplier's own submissions.
  for (const pkg of packages) {
    if (!pkg.id) continue;
    for (const sub of submissionsByPackage.get(pkg.id) ?? []) {
      if (sub.id && approvedIds.has(sub.id)) return true;
    }
  }
  return false;
}

function timberProjectHasAnyAward(
  project: Pick<RfqProject, 'approvedSubmissionIds' | 'approvedSubmissionIdsByRelease'>,
): boolean {
  if ((project.approvedSubmissionIds ?? []).some(Boolean)) return true;
  return Object.values(project.approvedSubmissionIdsByRelease ?? {}).some(
    ids => (ids ?? []).some(Boolean),
  );
}

function cncProjectHasAnyAward(
  project: Pick<
    RfqProject,
    'approvedSupplierOrgId' | 'approvedSupplierOrgIdByRelease' | 'approvedSupplierOrgIdsByRelease'
  >,
): boolean {
  if (project.approvedSupplierOrgId) return true;
  if (Object.values(project.approvedSupplierOrgIdByRelease ?? {}).some(Boolean)) return true;
  return Object.values(project.approvedSupplierOrgIdsByRelease ?? {}).some(
    ids => (ids ?? []).length > 0,
  );
}

function workGroupHasAnyAward(group: VendorPortalWorkGroup): boolean {
  if (normalizeWorkType(group.project.workType) === 'cnc_quote') {
    return cncProjectHasAnyAward(group.project);
  }
  return timberProjectHasAnyAward(group.project);
}

function workGroupHasRejectedQuote(
  group: VendorPortalWorkGroup,
  submissionsByPackage: Map<string, RfqSubmission[]>,
  orgId: string | undefined,
): boolean {
  if (!orgId) return false;
  for (const pkg of group.packages) {
    if (!pkg.id) continue;
    for (const sub of submissionsByPackage.get(pkg.id) ?? []) {
      if (sub.supplierOrgId && sub.supplierOrgId !== orgId) continue;
      if (isRfqQuoteRejected(sub)) return true;
    }
  }
  return false;
}

/** True when staff stamped a rejection on this supplier’s quote (not merely awarded elsewhere). */
export function workGroupHasStaffRejectedQuote(
  group: VendorPortalWorkGroup | null,
  submissionsByPackage: Map<string, RfqSubmission[]>,
  orgId: string | undefined,
): boolean {
  if (!group) return false;
  return workGroupHasRejectedQuote(group, submissionsByPackage, orgId);
}

/**
 * Someone else won this work type — this supplier did not win and may never
 * have quoted. Distinct from an explicit staff rejection of their quote.
 */
export function workGroupIsAwardedElsewhere(
  group: VendorPortalWorkGroup | null,
  submissionsByPackage: Map<string, RfqSubmission[]>,
  orgId: string | undefined,
): boolean {
  if (!group || !orgId) return false;
  if (workGroupHasApprovedQuote(group, submissionsByPackage, orgId)) return false;
  return workGroupHasAnyAward(group);
}

/** Someone else won this work type, or staff already marked this supplier’s quote rejected. */
export function workGroupIsLostAward(
  group: VendorPortalWorkGroup | null,
  submissionsByPackage: Map<string, RfqSubmission[]>,
  orgId: string | undefined,
): boolean {
  if (!group || !orgId) return false;
  if (workGroupHasApprovedQuote(group, submissionsByPackage, orgId)) return false;
  return workGroupHasAnyAward(group) || workGroupHasRejectedQuote(group, submissionsByPackage, orgId);
}

/**
 * Staff-entered rejection reason on this org's most recently rejected quote
 * for the work group (if any). Awarded-elsewhere without a stamped rejection
 * returns null — there is nothing to show on hover.
 */
export function workGroupRejectionReason(
  group: VendorPortalWorkGroup | null,
  submissionsByPackage: Map<string, RfqSubmission[]>,
  orgId: string | undefined,
): string | null {
  if (!group || !orgId) return null;
  let best: { at: number; reason: string } | null = null;
  for (const pkg of group.packages) {
    if (!pkg.id) continue;
    for (const sub of submissionsByPackage.get(pkg.id) ?? []) {
      if (sub.supplierOrgId && sub.supplierOrgId !== orgId) continue;
      if (!isRfqQuoteRejected(sub)) continue;
      const reason = (sub.rejectionReason || '').trim();
      if (!reason) continue;
      const at = sub.rejectedAt instanceof Date ? sub.rejectedAt.getTime() : 0;
      if (!best || at >= best.at) best = { at, reason };
    }
  }
  return best?.reason ?? null;
}

/** Hover copy for Closed Projects — one or both work-type reasons when present. */
export function dealLostReasonTooltip(
  deal: VendorPortalDealGroup,
  submissionsByPackage: Map<string, RfqSubmission[]>,
  orgId: string | undefined,
  opts?: { includeTimber?: boolean; includeCnc?: boolean },
): string | null {
  const includeTimber = opts?.includeTimber !== false;
  const includeCnc = opts?.includeCnc !== false;
  const timber = includeTimber
    ? workGroupRejectionReason(deal.timber, submissionsByPackage, orgId)
    : null;
  const cnc = includeCnc
    ? workGroupRejectionReason(deal.cnc, submissionsByPackage, orgId)
    : null;
  if (timber && cnc) {
    return timber === cnc ? timber : `Timber: ${timber}\nCNC: ${cnc}`;
  }
  return timber || cnc || null;
}

function cncGroupHasApproval(group: VendorPortalWorkGroup, orgId: string): boolean {
  const { project } = group;
  if (project.approvedSupplierOrgId === orgId) return true;
  for (const ids of Object.values(project.approvedSupplierOrgIdsByRelease ?? {})) {
    if (ids.includes(orgId)) return true;
  }
  for (const id of Object.values(project.approvedSupplierOrgIdByRelease ?? {})) {
    if (id === orgId) return true;
  }
  return false;
}

/** True when any of this supplier's quotes on the work group has been approved. */
export function workGroupHasApprovedQuote(
  group: VendorPortalWorkGroup | null,
  submissionsByPackage: Map<string, RfqSubmission[]>,
  orgId: string | undefined,
): boolean {
  if (!group || !orgId) return false;
  if (normalizeWorkType(group.project.workType) === 'cnc_quote') {
    return cncGroupHasApproval(group, orgId);
  }
  return timberGroupHasApproval(group, submissionsByPackage);
}

/** True when this package version has an approved quote for this supplier. */
export function packageHasApprovedQuoteForOrg(
  project: RfqProject,
  pkg: RfqPackage,
  packages: RfqPackage[],
  submissionsByPackage: Map<string, RfqSubmission[]>,
  orgId: string | undefined,
): boolean {
  if (!orgId) return false;
  if (normalizeWorkType(pkg.workType) === 'cnc_quote') {
    return isCncPackageApprovedForOrg(project, pkg, orgId);
  }
  const approvedIds = approvedSubmissionIdsForPackage(
    project,
    pkg,
    packages,
    submissionsByPackage,
  );
  if (!approvedIds.size) return false;
  const subs = pkg.id ? submissionsByPackage.get(pkg.id) ?? [] : [];
  return subs.some(s => s.id && approvedIds.has(s.id));
}

/** Package ids for versions that hold this supplier's approved quote. */
export function approvedPackageIdsForWorkGroup(
  group: VendorPortalWorkGroup,
  submissionsByPackage: Map<string, RfqSubmission[]>,
  orgId: string | undefined,
): Set<string> {
  const out = new Set<string>();
  if (!orgId) return out;
  const { project, packages } = group;
  for (const pkg of packages) {
    if (!pkg.id) continue;
    if (packageHasApprovedQuoteForOrg(project, pkg, packages, submissionsByPackage, orgId)) {
      out.add(pkg.id);
    }
  }
  return out;
}

export type VendorPortalDealSidebarStatus = 'needs_quote' | 'quoted' | 'approved' | 'bid_lost';

/** Hub closed the deal (rfqProject.status synced from Project Hub). */
export function isDealClosedForSupplier(deal: VendorPortalDealGroup): boolean {
  const timberClosed = deal.timber?.project.status === 'closed';
  const cncClosed = deal.cnc?.project.status === 'closed';
  return !!timberClosed || !!cncClosed;
}

/** Closed or awarded-elsewhere deals where this supplier did not win move to Closed Projects. */
export function isDealArchivedForSupplier(
  deal: VendorPortalDealGroup,
  submissionsByPackage: Map<string, RfqSubmission[]>,
  orgId: string | undefined,
): boolean {
  if (dealHasApprovedQuote(deal, submissionsByPackage, orgId)) return false;
  if (isDealClosedForSupplier(deal)) return true;
  const timberInPlay = !!deal.timber && !workGroupIsLostAward(deal.timber, submissionsByPackage, orgId);
  const cncInPlay = !!deal.cnc && !workGroupIsLostAward(deal.cnc, submissionsByPackage, orgId);
  if (timberInPlay || cncInPlay) return false;
  return (
    workGroupIsLostAward(deal.timber, submissionsByPackage, orgId)
    || workGroupIsLostAward(deal.cnc, submissionsByPackage, orgId)
  );
}

export type VendorPortalWorkGroupTableStatus =
  | 'requested'
  | 'quoted'
  | 'approved'
  | 'declined'
  | 'not_awarded';

/** One-word sidebar table status for a single work group (timber or CNC). */
export function workGroupTableStatus(
  group: VendorPortalWorkGroup,
  submissionsByPackage: Map<string, RfqSubmission[]>,
  orgId: string | undefined,
): VendorPortalWorkGroupTableStatus {
  if (workGroupHasApprovedQuote(group, submissionsByPackage, orgId)) {
    return 'approved';
  }
  if (workGroupHasRejectedQuote(group, submissionsByPackage, orgId)) {
    return 'declined';
  }
  if (workGroupHasAnyAward(group)) {
    return 'not_awarded';
  }
  const versions = buildVendorPackageVersions(group.packages, submissionsByPackage);
  const latest = versions.find(v => v.isLatest) ?? versions[0];
  if (latest?.quoteStatus === 'not_bidding') {
    return 'declined';
  }
  if (
    projectNeedsQuoteResponse(
      group.packages,
      submissionsByPackage,
      group.project.status,
    )
  ) {
    return 'requested';
  }
  return 'quoted';
}

export function sidebarTableStatusLabel(status: VendorPortalWorkGroupTableStatus): string {
  switch (status) {
    case 'requested':
      return 'Requested';
    case 'quoted':
      return 'Quoted';
    case 'approved':
      return 'Approved';
    case 'declined':
      return 'Declined';
    case 'not_awarded':
      return 'Not awarded';
  }
}

/** Sidebar badge for a single work group (timber or CNC). */
export function workGroupSidebarStatus(
  group: VendorPortalWorkGroup,
  submissionsByPackage: Map<string, RfqSubmission[]>,
  orgId: string | undefined,
): VendorPortalDealSidebarStatus {
  if (
    projectNeedsQuoteResponse(
      group.packages,
      submissionsByPackage,
      group.project.status,
    )
  ) {
    return 'needs_quote';
  }
  if (workGroupHasApprovedQuote(group, submissionsByPackage, orgId)) {
    return 'approved';
  }
  return 'quoted';
}

export function sidebarStatusBadgeClass(status: VendorPortalDealSidebarStatus): string {
  return status === 'needs_quote' ? 'needs-quote' : status;
}

export function sidebarStatusLabel(status: VendorPortalDealSidebarStatus): string {
  switch (status) {
    case 'needs_quote':
      return 'Needs quote';
    case 'approved':
      return 'Approved';
    case 'quoted':
      return 'Quoted';
    case 'bid_lost':
      return 'LCTF Bid Lost';
  }
}

/** Sidebar badge for an active (non-archived) deal row. */
export function dealSidebarStatus(
  deal: VendorPortalDealGroup,
  submissionsByPackage: Map<string, RfqSubmission[]>,
  orgId: string | undefined,
  needsQuote: boolean,
): VendorPortalDealSidebarStatus {
  if (needsQuote) return 'needs_quote';
  if (dealHasApprovedQuote(deal, submissionsByPackage, orgId)) return 'approved';
  return 'quoted';
}

/** Sidebar badge — true when the deal has any approved quote for this supplier. */
export function dealHasApprovedQuote(
  group: VendorPortalDealGroup,
  submissionsByPackage: Map<string, RfqSubmission[]>,
  orgId: string | undefined,
): boolean {
  return (
    workGroupHasApprovedQuote(group.timber, submissionsByPackage, orgId)
    || workGroupHasApprovedQuote(group.cnc, submissionsByPackage, orgId)
  );
}

/** Supplier-facing label for an approved timber submission. */
export function timberSubmissionApprovedBadgeLabel(
  project: Pick<RfqProject, 'approvedSubmissionApproverInitials'>,
  submissionId: string | undefined,
): string {
  if (!submissionId) return 'Approved';
  const initials = project.approvedSubmissionApproverInitials?.[submissionId]?.trim();
  return initials ? `Approved by ${initials}` : 'Approved';
}

/** Supplier-facing label when a CNC package was awarded to this org. */
export function cncPackageApprovedBadgeLabel(
  project: Pick<RfqProject, 'approvedSupplierApproverInitialsByRelease'>,
  pkg: RfqPackage,
  orgId: string | undefined,
): string {
  if (!orgId) return 'Approved';
  const releaseId = releaseKeyForPackage(pkg);
  const initials = project.approvedSupplierApproverInitialsByRelease?.[releaseId]?.[orgId]?.trim();
  return initials ? `Approved by ${initials}` : 'Approved';
}

/** Badge label for a package version row in the supplier portal version tree. */
export function packageApprovedBadgeLabel(
  project: RfqProject,
  pkg: RfqPackage,
  packages: RfqPackage[],
  submissionsByPackage: Map<string, RfqSubmission[]>,
  orgId: string | undefined,
): string {
  if (!orgId) return 'Approved';
  if (normalizeWorkType(pkg.workType) === 'cnc_quote') {
    return cncPackageApprovedBadgeLabel(project, pkg, orgId);
  }
  const approvedIds = approvedSubmissionIdsForPackage(
    project,
    pkg,
    packages,
    submissionsByPackage,
  );
  for (const id of Array.from(approvedIds)) {
    const label = timberSubmissionApprovedBadgeLabel(project, id);
    if (label !== 'Approved') return label;
  }
  return 'Approved';
}
